import React, { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeaderUserMenu, HeaderUserMenuHandle } from './HeaderUserMenu';
import { useUser, useUserPreferences } from '../../contexts/UserContext';
import { useCommands } from '../../contexts/CommandContext';
import { resolveActiveTheme } from '../../utils/themePreference';
import { AppHeader } from '../UI/nav';
import { HeaderChatPanelShortcut } from '../ChatPanel/HeaderChatPanelShortcut';
import { CONTROL_BRIDGE_HOME_PATH } from '../../utils/controlBridgeRoutes';
import styles from './UnifiedHeader.module.css';
import './UnifiedHeader.css';

interface UnifiedHeaderProps {
  showNavigation?: boolean;
  showUserControls?: boolean;
  onMenuToggle?: () => void;
  className?: string;
}

export const UnifiedHeader: React.FC<UnifiedHeaderProps> = ({
  showNavigation = true,
  showUserControls = true,
  onMenuToggle,
  className = '',
}) => {
  const navigate = useNavigate();
  const accountMenuRef = useRef<HeaderUserMenuHandle>(null);
  const { isAuthenticated, selectedCompany } = useUser();
  const { preferences, updatePreferences } = useUserPreferences();
  const { setShowCommandHelp } = useCommands();

  const theme = resolveActiveTheme(preferences.theme);

  const needsCompanySelection = isAuthenticated && !selectedCompany?.name;

  const brandTitle = useMemo(() => {
    if (selectedCompany?.name) {
      return selectedCompany.name;
    }
    return isAuthenticated ? 'Select company' : 'Krysalis';
  }, [isAuthenticated, selectedCompany?.name]);

  const handleBrandClick = () => {
    if (needsCompanySelection) {
      accountMenuRef.current?.openAccountMenu();
      return;
    }
    navigate(isAuthenticated ? CONTROL_BRIDGE_HOME_PATH : '/');
  };

  const logo = (
    <button
      type="button"
      className={`${styles.brandButton}${needsCompanySelection ? ` ${styles.brandButtonNeedsCompany}` : ''}`}
      onClick={handleBrandClick}
      aria-label={needsCompanySelection ? 'Select company — open account menu' : `${brandTitle} home`}
      title={needsCompanySelection ? 'Click to open company selector' : brandTitle}
    >
      <img src="/krysalis-logo.svg" alt="" className={styles.brandLogo} />
      <span className={styles.brandTitle}>{brandTitle}</span>
    </button>
  );

  return (
    <AppHeader
      className={className}
      logo={logo}
      onMenu={showNavigation && onMenuToggle ? onMenuToggle : undefined}
      onSearchClick={() => setShowCommandHelp(true)}
      onSignalsClick={isAuthenticated ? () => navigate('/render/control-bridge') : undefined}
      actions={showNavigation && isAuthenticated ? <HeaderChatPanelShortcut /> : undefined}
      theme={theme}
      onToggleTheme={() => {
        void updatePreferences({ theme: theme === 'dark' ? 'light' : 'dark' });
      }}
      userSlot={showUserControls ? <HeaderUserMenu ref={accountMenuRef} variant="appHeader" /> : undefined}
    />
  );
};

export default UnifiedHeader;
